'use client';

/**
 * Popover — anchored floating panel (CRM redesign).
 *
 * Used for explainable surfaces: the HealthBadge breakdown, edit-history
 * provenance, tag pickers. Renders into a portal with fixed positioning so it
 * NEVER shifts page layout. Flips above the anchor when there is no room
 * below, clamps inside the viewport, repositions on scroll/resize, and closes
 * on outside-click or Esc (returning focus to the anchor).
 *
 * Keyboard: on open, focus moves to the first focusable item (consumers that
 * focus their own element first win); ArrowUp/ArrowDown rove focus through
 * the panel's focusable items while focus is inside the panel or on the
 * anchor; Esc restores focus to the anchor.
 *
 * Elevation comes from the surface ladder (raised #1A2027 + hairline border),
 * never from shadows.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

import { cn } from '@/lib/utils';

export type PopoverAnchor = HTMLElement | RefObject<HTMLElement | null> | null;

export interface PopoverProps {
  open: boolean;
  /** The element (or ref to it) the panel is anchored beneath. */
  anchor: PopoverAnchor;
  onClose: () => void;
  /** Horizontal alignment against the anchor edge. Default 'start'. */
  align?: 'start' | 'end';
  children: ReactNode;
  className?: string;
}

const GAP = 6;
const VIEWPORT_MARGIN = 8;

/** Keyboard-reachable descendants — menu items, filter inputs, links. */
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

function resolveAnchor(anchor: PopoverAnchor): HTMLElement | null {
  if (!anchor) return null;
  if (typeof HTMLElement !== 'undefined' && anchor instanceof HTMLElement) return anchor;
  return (anchor as RefObject<HTMLElement | null>).current ?? null;
}

export function Popover({
  open,
  anchor,
  onClose,
  align = 'start',
  children,
  className,
}: PopoverProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const reposition = useCallback(() => {
    const anchorEl = resolveAnchor(anchor);
    const panel = panelRef.current;
    if (!anchorEl || !panel) return;

    const rect = anchorEl.getBoundingClientRect();
    const panelWidth = panel.offsetWidth;
    const panelHeight = panel.offsetHeight;

    // Below the anchor by default; flip above when it doesn't fit and there IS
    // room above. Clamp to the viewport either way.
    let top = rect.bottom + GAP;
    if (
      top + panelHeight > window.innerHeight - VIEWPORT_MARGIN &&
      rect.top - GAP - panelHeight >= VIEWPORT_MARGIN
    ) {
      top = rect.top - GAP - panelHeight;
    }
    top = Math.min(
      Math.max(top, VIEWPORT_MARGIN),
      window.innerHeight - panelHeight - VIEWPORT_MARGIN,
    );

    let left = align === 'end' ? rect.right - panelWidth : rect.left;
    left = Math.min(
      Math.max(left, VIEWPORT_MARGIN),
      window.innerWidth - panelWidth - VIEWPORT_MARGIN,
    );

    setPosition({ top: Math.round(top), left: Math.round(left) });
  }, [anchor, align]);

  // Measure-then-show: the panel renders hidden first so flip math can use
  // its real size, then becomes visible at the computed coordinates.
  useLayoutEffect(() => {
    if (open) {
      reposition();
    } else {
      setPosition(null);
    }
  }, [open, reposition]);

  // Track viewport changes while open.
  useEffect(() => {
    if (!open) return;
    window.addEventListener('resize', reposition);
    document.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      document.removeEventListener('scroll', reposition, true);
    };
  }, [open, reposition]);

  // Keyboard operability: a popover menu must be usable without a mouse.
  // On open, move focus to the first focusable item (the panel portals to the
  // end of <body>, so without this Tab from the anchor walks the whole page
  // before reaching the menu). Consumers that focus something themselves
  // (e.g. the tag editor's filter input) win — we only focus when focus is
  // still outside the panel. Falls back to the panel itself (tabIndex=-1) for
  // purely informational popovers.
  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => {
      const panel = panelRef.current;
      if (!panel) return;
      if (panel.contains(document.activeElement)) return;
      const first = panel.querySelector<HTMLElement>(FOCUSABLE_SELECTOR);
      (first ?? panel).focus();
    });
    return () => cancelAnimationFrame(raf);
  }, [open]);

  // Outside-click (ignoring the anchor so its own toggle works) + Esc +
  // ArrowUp/ArrowDown roving focus through the panel's focusable items.
  useEffect(() => {
    if (!open) return;

    function onPointerDown(event: PointerEvent) {
      const target = event.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (resolveAnchor(anchor)?.contains(target)) return;
      onClose();
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        resolveAnchor(anchor)?.focus?.();
        return;
      }

      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      const panel = panelRef.current;
      if (!panel) return;
      const active = document.activeElement;
      const anchorEl = resolveAnchor(anchor);
      // Only steer arrows for the open menu — focus must sit inside the panel
      // or on its anchor; never hijack arrows elsewhere on the page.
      const inPanel = active instanceof HTMLElement && panel.contains(active);
      const onAnchor = Boolean(
        anchorEl && active instanceof HTMLElement && anchorEl.contains(active),
      );
      if (!inPanel && !onAnchor) return;
      // Selects/textareas own their arrow keys.
      if (
        active instanceof HTMLElement &&
        (active.tagName === 'SELECT' || active.tagName === 'TEXTAREA')
      ) {
        return;
      }
      const items = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR));
      if (items.length === 0) return;
      event.preventDefault();
      const delta = event.key === 'ArrowDown' ? 1 : -1;
      const index = inPanel ? items.indexOf(active as HTMLElement) : -1;
      const next =
        index === -1
          ? delta === 1
            ? 0
            : items.length - 1
          : Math.min(items.length - 1, Math.max(0, index + delta));
      items[next]?.focus();
    }

    document.addEventListener('pointerdown', onPointerDown, true);
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true);
      document.removeEventListener('keydown', onKeyDown, true);
    };
  }, [open, anchor, onClose]);

  if (!open || typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={panelRef}
      role="dialog"
      tabIndex={-1}
      style={{
        top: position?.top ?? -9999,
        left: position?.left ?? -9999,
        visibility: position ? 'visible' : 'hidden',
      }}
      className={cn(
        'fixed z-50 min-w-[180px] max-w-[min(360px,calc(100vw-16px))] rounded-[6px] border border-[var(--color-hair)] bg-[var(--color-surface-2)] text-[13px] text-[var(--color-text)] outline-none transition-opacity duration-150 motion-reduce:transition-none',
        position ? 'opacity-100' : 'opacity-0',
        className,
      )}
    >
      {children}
    </div>,
    document.body,
  );
}
